'use client'

import type { ReactNode } from 'react'
import { MdClose, MdSearch } from 'react-icons/md'

/**
 * Peças compartilhadas das listagens (exames, consultas, procedimentos,
 * pacotes). As quatro telas eram cópias uma da outra, cada uma com o próprio
 * gradiente fora do tema e a própria régua de espaçamento — o que divergia
 * entre elas era acidente, não decisão.
 */

export interface ListPageHeaderProps {
    icon: ReactNode
    title: string
    /** Linha de apoio: normalmente a contagem de registros. */
    subtitle?: ReactNode
    /** Busca embutida. Sem ela o campo não é renderizado. */
    search?: {
        value: string
        onChange: (value: string) => void
        placeholder?: string
    }
    /** Botões de ação à direita (criar, importar, atalhos). */
    actions?: ReactNode
}

/**
 * Cabeçalho de listagem: identidade à esquerda, busca sempre visível no meio e
 * ações à direita.
 *
 * A busca não se esconde mais atrás de um botão que trocava o cabeçalho
 * inteiro — abrir a busca fazia sumir "Novo" e "Importar", e voltar exigia
 * fechar o campo.
 */
export function ListPageHeader({
    icon,
    title,
    subtitle,
    search,
    actions,
}: ListPageHeaderProps) {
    return (
        <header className="relative overflow-hidden rounded-2xl border border-base-300 bg-base-100">
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 bg-linear-to-r from-secondary/10 via-primary/5 to-transparent"
            />
            <div className="relative flex flex-wrap items-center gap-4 p-5">
                <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-secondary/10 text-secondary ring-1 ring-secondary/20">
                    {icon}
                </div>
                <div className="min-w-0 flex-1">
                    <h1 className="truncate text-xl font-bold text-base-content sm:text-2xl">
                        {title}
                    </h1>
                    {subtitle && (
                        <p className="text-sm text-base-content/50">
                            {subtitle}
                        </p>
                    )}
                </div>

                {search && (
                    <div className="relative order-last w-full sm:order-none sm:w-64">
                        {/* z-10: sem isso o ícone fica atrás do fundo do input
                            e some (mesmo motivo do ListSortSelect). */}
                        <MdSearch className="pointer-events-none absolute left-3 top-1/2 z-10 -translate-y-1/2 text-lg text-base-content/40" />
                        <input
                            type="search"
                            value={search.value}
                            onChange={e => search.onChange(e.target.value)}
                            placeholder={search.placeholder || 'Buscar...'}
                            className="input input-bordered input-sm w-full pl-9 pr-9"
                        />
                        {search.value && (
                            <button
                                type="button"
                                onClick={() => search.onChange('')}
                                className="btn btn-ghost btn-xs btn-circle absolute right-1 top-1/2 z-10 -translate-y-1/2"
                                title="Limpar busca"
                            >
                                <MdClose className="text-base" />
                            </button>
                        )}
                    </div>
                )}

                {actions && (
                    <div className="flex flex-wrap items-center gap-2">
                        {actions}
                    </div>
                )}
            </div>
        </header>
    )
}

export interface FilterFieldProps {
    label: string
    htmlFor?: string
    /** Largura do controle. Use as mesmas medidas entre telas irmãs. */
    className?: string
    children: ReactNode
}

/**
 * Campo da barra de filtros. Mesma marcação de `ListSortSelect` e
 * `DateRangeFilter`, para que tudo na barra tenha rótulo e altura iguais —
 * antes convivia campo com rótulo próprio, campo com rótulo embutido no
 * componente e campo sem rótulo nenhum, alinhados na marra por `items-end`.
 */
export function FilterField({
    label,
    htmlFor,
    className = '',
    children,
}: FilterFieldProps) {
    return (
        <div className={`form-control ${className}`}>
            <label className="label py-0" htmlFor={htmlFor}>
                <span className="label-text text-xs font-medium">{label}</span>
            </label>
            {children}
        </div>
    )
}

export interface ListFilterBarProps {
    children: ReactNode
    /** Exibido quando há filtro ativo, ao lado do botão de limpar. */
    activeCount?: number
    onClear?: () => void
}

/** Barra de filtros: um bloco só, em vez de campos soltos sobre o fundo. */
export function ListFilterBar({
    children,
    activeCount = 0,
    onClear,
}: ListFilterBarProps) {
    return (
        <div className="flex flex-wrap items-end gap-3 rounded-2xl border border-base-300 bg-base-100 p-4">
            {children}
            {activeCount > 0 && onClear && (
                <button
                    type="button"
                    onClick={onClear}
                    className="btn btn-ghost btn-sm gap-1.5 text-base-content/60"
                    title="Limpar todos os filtros"
                >
                    <MdClose className="text-base" />
                    Limpar filtros
                    <span className="badge badge-sm badge-neutral">
                        {activeCount}
                    </span>
                </button>
            )}
        </div>
    )
}

export interface ListEmptyStateProps {
    icon: ReactNode
    /** Texto quando não há nada cadastrado. */
    title: string
    description?: string
    action?: ReactNode
    /** Com filtro ativo a mensagem muda: o vazio é do filtro, não do cadastro. */
    filtered?: boolean
    onClearFilters?: () => void
}

export function ListEmptyState({
    icon,
    title,
    description,
    action,
    filtered = false,
    onClearFilters,
}: ListEmptyStateProps) {
    return (
        <div className="rounded-2xl border border-base-300 bg-base-100 p-12 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-2xl bg-base-200 text-3xl text-base-content/30">
                {icon}
            </div>
            <h3 className="mb-2 text-lg font-semibold text-base-content">
                {filtered ? 'Nenhum resultado para esses filtros' : title}
            </h3>
            <p className="mb-6 text-sm text-base-content/50">
                {filtered
                    ? 'Ajuste ou limpe os filtros para ver mais registros.'
                    : description}
            </p>
            {filtered
                ? onClearFilters && (
                      <button
                          type="button"
                          onClick={onClearFilters}
                          className="btn btn-outline btn-sm"
                      >
                          Limpar filtros
                      </button>
                  )
                : action}
        </div>
    )
}
